import { useContext, useEffect, useState } from 'react';
import { PageTitleContext } from 'context/PageTitleContext';
import { CrumbsContext } from 'context/CrumbsContext';
import { Backend } from 'api/ApiConsumer';
import { API_ROUTES } from 'routes/api';
import { NotificationContext } from 'context/NotificationContext';
import {
  CogIcon,
  DownloadIcon,
  MailIcon,
  UploadIcon,
} from '@heroicons/react/outline';
import * as XLSX from 'xlsx';

export default function EventsUpload() {
  let { setTitle } = useContext(PageTitleContext);
  let { setCrumbs } = useContext(CrumbsContext);
  let { setNotification } = useContext(NotificationContext);


  const [form, setForm] = useState({
    title: '',
    description: '',
    event: '',
    eventOrganiser: '',
    eventDate: '',
    licensingOfficial: '',
    file: null,
  });
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [recordings, setRecordings] = useState([]);

  useEffect(() => {
    setTitle('Upload Event Recording');
    setCrumbs([
      { name: 'Events & Reporting', link: '/events-manager/upload-event-recording' },
    ]);
    fetchRecordings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function fetchRecordings() {
    Backend.get(API_ROUTES.EVENTS_MANAGER.RECORDINGS)
      .then((res) => {
        setRecordings(res.data || []);
      })
      .catch((err) => {
        console.error('Error fetching recordings:', err);
        setNotification({
          show: true,
          title: 'Error',
          message: 'Could not load event recordings.',
          type: 'error',
        });
      });
  }

  function downloadExcelReport(recording) {
    // Get results array (direct array, not results.tracks)
    const results = Array.isArray(recording.results) ? recording.results : [];

    if (results.length === 0) {
      setNotification({
        show: true,
        title: 'No data',
        message: 'No results available for this recording.',
        type: 'error',
      });
      return;
    }

    // Format duration from milliseconds to MM:SS
    const formatDuration = (ms) => {
      if (!ms) return 'N/A';
      const totalSeconds = Math.floor(ms / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    // Prepare data for Excel
    const excelData = results.map((track) => ({
      Title: track.title || 'N/A',
      Artist: track.artist || 'N/A',
      Label: track.label || 'N/A',
      Duration: formatDuration(track.duration_ms),
      Genre: track.genre || 'N/A',
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Set column widths
    ws['!cols'] = [
      { wch: 30 }, // Title
      { wch: 25 }, // Artist
      { wch: 30 }, // Label
      { wch: 15 }, // Duration
      { wch: 20 }, // Genre
    ];

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Results');

    // Generate filename from recording title
    const fileName = `${(recording.title || 'Event-Recording')
      .replace(/[^a-z0-9]/gi, '_')
      .toLowerCase()}.xlsx`;

    // Write and download
    XLSX.writeFile(wb, fileName);
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!form.title || !form.file) {
      setNotification({
        show: true,
        title: 'Missing information',
        message: 'Please provide a title and select a recording file.',
        type: 'error',
      });
      return;
    }

    const data = new FormData();
    data.append('title', form.title);
    data.append('description', form.description);
    data.append('event', form.event);
    data.append('eventOrganiser', form.eventOrganiser);
    data.append('eventDate', form.eventDate);
    data.append('licensingOfficial', form.licensingOfficial);
    data.append('recording', form.file);

    setUploading(true);
    setProgress(0);

    Backend.post(API_ROUTES.EVENTS_MANAGER.RECORDINGS, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (evt) => {
        if (!evt.total) return;
        const percentCompleted = Math.round((evt.loaded * 100) / evt.total);
        setProgress(percentCompleted);
      },
    })
      .then(() => {
        setNotification({
          show: true,
          title: 'Upload successful',
          message:
            'Your recording has been uploaded and is being processed. The latest file appears at the top of the list.',
          type: 'success',
        });
        setForm({
          title: '',
          description: '',
          event: '',
          eventOrganiser: '',
          eventDate: '',
          licensingOfficial: '',
          file: null,
        });
        setProgress(0);
        fetchRecordings();
      })
      .catch(() => {
        setNotification({
          show: true,
          title: 'Upload failed',
          message: 'Could not upload your recording, please try again later.',
          type: 'error',
        });
      })
      .finally(() => setUploading(false));
  }

  return (
    <div className="min-h-full px-5 py-4 mt-0">
      <div className="bg-white shadow sm:rounded-lg p-6 mb-6">
        <h2 className="text-lg leading-6 font-semibold text-gray-900 mb-1">
          Upload Event Recording
        </h2>
          <p className="text-sm text-gray-500 mb-4">
            Provide basic details and upload your event recording. The most
            recently processed file will appear at the top of the list below.
          </p>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Recording Title
            </label>
            <input
              type="text"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              value={form.title}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, title: e.target.value }))
              }
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Recording Description
            </label>
            <textarea
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              rows={3}
              value={form.description}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, description: e.target.value }))
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Event
            </label>
            <input
              type="text"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              value={form.event}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, event: e.target.value }))
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Event Organiser/Promoter
            </label>
            <input
              type="text"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              value={form.eventOrganiser}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, eventOrganiser: e.target.value }))
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Event Date
            </label>
            <input
              type="date"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              value={form.eventDate}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, eventDate: e.target.value }))
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Licensing Official
            </label>
            <input
              type="text"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              value={form.licensingOfficial}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  licensingOfficial: e.target.value,
                }))
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Choose Recording File
            </label>
            <input
              type="file"
              accept="audio/*"
              className="mt-1 block w-full text-sm text-gray-700"
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  file: e.target.files && e.target.files[0],
                }))
              }
            />
          </div>

          {uploading && (
            <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
              <div
                className="bg-blue-600 h-2.5 rounded-full"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={uploading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-500 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <CogIcon className="animate-spin h-5 w-5 mr-2" />
              ) : (
                <UploadIcon className="h-5 w-5 mr-2" />
              )}
              {uploading ? 'Uploading...' : 'Upload Recording'}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white shadow sm:rounded-lg p-6 pb-6">
        <h3 className="text-md leading-6 font-semibold text-gray-900 mb-1">
          Uploaded Recordings
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Track the status of your uploaded event recordings. Files move from
          <span className="font-semibold"> processing</span> to
          <span className="font-semibold"> complete</span> once ready, after
          which you can download or email the report.
        </p>
        <div className="overflow-x-auto overflow-y-visible pb-2">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3.5 text-left font-medium text-gray-700">
                  Recording Title
                </th>
                <th className="px-4 py-3.5 text-left font-medium text-gray-700">
                  Description
                </th>
                <th className="px-4 py-3.5 text-left font-medium text-gray-700">
                  Date Uploaded
                </th>
                <th className="px-4 py-3.5 text-left font-medium text-gray-700">
                  Status
                </th>
                <th className="px-4 py-3.5 text-right font-medium text-gray-700 whitespace-nowrap min-w-[120px]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recordings.map((rec) => {
                const isComplete = !!rec.processed;
                const createdDate = rec.created
                  ? new Date(rec.created).toLocaleString()
                  : '';
                return (
                  <tr key={rec.id}>
                    <td className="px-4 py-5 whitespace-nowrap">
                      {rec.title}
                    </td>
                    <td className="px-4 py-5 whitespace-nowrap">
                      {rec.description}
                    </td>
                    <td className="px-4 py-5 whitespace-nowrap">
                      {createdDate}
                    </td>
                    <td className="px-4 py-5 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium ${
                          isComplete
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {isComplete ? 'complete' : 'processing'}
                      </span>
                    </td>
                    <td className="px-4 py-5 whitespace-nowrap text-right text-sm font-medium space-x-2 min-w-[120px]">
                      <button
                        type="button"
                        disabled={!isComplete}
                        onClick={() => {
                          if (!isComplete) return;
                          downloadExcelReport(rec);
                        }}
                        className={`inline-flex items-center px-3 py-2 border border-transparent text-xs rounded-md shadow-sm ${
                          isComplete
                            ? 'text-blue-700 bg-blue-100 hover:bg-blue-200'
                            : 'text-gray-400 bg-gray-100 cursor-not-allowed'
                        }`}
                      >
                        <DownloadIcon className="h-4 w-4 mr-1" />
                        Download
                      </button>
                      <button
                        type="button"
                        disabled={!isComplete}
                        onClick={() => {
                          if (!isComplete) return;
                          Backend.post(
                            API_ROUTES.EVENTS_MANAGER.REPORT_EMAIL(rec.id),
                          )
                            .then(() => {
                              setNotification({
                                show: true,
                                title: 'Email sent',
                                message:
                                  'The report will be emailed to your registered address.',
                                type: 'success',
                              });
                            })
                            .catch(() => {
                              setNotification({
                                show: true,
                                title: 'Email failed',
                                message:
                                  'Could not email the report, please try again later.',
                                type: 'error',
                              });
                            });
                        }}
                        className={`inline-flex items-center px-3 py-2 border border-transparent text-xs rounded-md shadow-sm ${
                          isComplete
                            ? 'text-indigo-700 bg-indigo-100 hover:bg-indigo-200'
                            : 'text-gray-400 bg-gray-100 cursor-not-allowed'
                        }`}
                      >
                        <MailIcon className="h-4 w-4 mr-1" />
                        Email
                      </button>
                    </td>
                  </tr>
                );
              })}
              {recordings.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-6 text-center text-gray-500"
                  >
                    No recordings uploaded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


